import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { Theme } from "@emotion/react";
import { mdsThemeHarvest } from "@/mds/themes/harvest";
import { mdsThemeHarvestMoon } from "@/mds/themes/harvest-moon";
import { MemCommonEditorTheme } from "@mem-labs/common-editor";

export enum ThemeType {
  Harvest = "harvest",
  HarvestMoon = "harvest-moon",
  System = "system",
}

interface ThemeContextType {
  theme: Theme;
  /** The resolved theme — always Harvest or HarvestMoon (never System). */
  themeType: ThemeType;
  /** The user's preference — can be Harvest, HarvestMoon, or System. */
  themePreference: ThemeType;
  toggleTheme: () => void;
  setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeType;
  getPersistedTheme?: () => ThemeType | null;
  persistTheme?: (theme: ThemeType) => void;
  persistedThemeStorageKey?: string;
}

/** Reads the current OS color-scheme preference via matchMedia. */
function getSystemTheme(): ThemeType.Harvest | ThemeType.HarvestMoon {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return ThemeType.Harvest;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? ThemeType.HarvestMoon
    : ThemeType.Harvest;
}

/** Resolves a preference to a concrete theme. */
function resolveTheme(preference: ThemeType): ThemeType.Harvest | ThemeType.HarvestMoon {
  if (preference === ThemeType.System) {
    return getSystemTheme();
  }
  return preference === ThemeType.HarvestMoon ? ThemeType.HarvestMoon : ThemeType.Harvest;
}

function isThemeType(value: ThemeType | null | undefined): value is ThemeType {
  return (
    value === ThemeType.Harvest || value === ThemeType.HarvestMoon || value === ThemeType.System
  );
}

export const ThemeContextProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = ThemeType.Harvest,
  getPersistedTheme,
  persistTheme,
  persistedThemeStorageKey,
}) => {
  const readThemePreference = useCallback((): ThemeType => {
    const value = getPersistedTheme?.();
    if (isThemeType(value)) {
      return value;
    }
    return defaultTheme;
  }, [defaultTheme, getPersistedTheme]);

  const [themePreference, setThemePreference] = useState<ThemeType>(() => readThemePreference());

  const [resolvedTheme, setResolvedTheme] = useState<ThemeType.Harvest | ThemeType.HarvestMoon>(
    () => resolveTheme(themePreference)
  );

  // Listen for OS preference changes when System is selected
  useEffect(() => {
    if (themePreference !== ThemeType.System) {
      setResolvedTheme(resolveTheme(themePreference));
      return;
    }

    // Initial resolve
    setResolvedTheme(getSystemTheme());

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: { matches: boolean }) => {
      setResolvedTheme(e.matches ? ThemeType.HarvestMoon : ThemeType.Harvest);
    };

    mediaQuery.addEventListener("change", handler);
    return () => {
      mediaQuery.removeEventListener("change", handler);
    };
  }, [themePreference]);

  const theme = resolvedTheme === ThemeType.HarvestMoon ? mdsThemeHarvestMoon : mdsThemeHarvest;

  // Persist theme preference changes
  useEffect(() => {
    persistTheme?.(themePreference);
  }, [themePreference, persistTheme]);

  useEffect(() => {
    if (!getPersistedTheme) return;

    const handleStorage = (event: StorageEvent) => {
      if (
        persistedThemeStorageKey &&
        event.key !== null &&
        event.key !== persistedThemeStorageKey
      ) {
        return;
      }

      setThemePreference(readThemePreference());
    };

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [getPersistedTheme, persistedThemeStorageKey, readThemePreference]);

  // Set color-scheme CSS property on :root element based on resolved theme
  useEffect(() => {
    const rootElement = document.documentElement;

    if (resolvedTheme === ThemeType.HarvestMoon) {
      rootElement.style.colorScheme = "dark";
    } else {
      rootElement.style.colorScheme = "light";
    }

    return () => {
      rootElement.style.colorScheme = "";
    };
  }, [resolvedTheme]);

  const toggleTheme = useCallback(() => {
    setThemePreference(prev => {
      if (prev === ThemeType.HarvestMoon) return ThemeType.Harvest;
      return ThemeType.HarvestMoon;
    });
  }, []);

  const setTheme = useCallback((newTheme: ThemeType) => {
    setThemePreference(newTheme);
  }, []);

  const value: ThemeContextType = {
    theme,
    themeType: resolvedTheme,
    themePreference,
    toggleTheme,
    setTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeContextProvider");
  }
  return context;
};

export const useMemCommonEditorTheme = (): MemCommonEditorTheme => {
  const { themeType } = useTheme();
  return themeType === ThemeType.HarvestMoon
    ? MemCommonEditorTheme.HarvestMoon
    : MemCommonEditorTheme.Harvest;
};
